/* colour-cards.js — collection cards show a known shopper their colour.
 *
 * color-pref.js resolves the shopper's preferred colours (window.SE_colorPref
 * + SE_colorPrefReady). This layer reads the catalog-wide colour map baked by
 * Agilite Media (handle -> colour -> variant + image, from the storefront's
 * own products.json truth) and swaps each product card's IMAGE to that
 * product's picture in the shopper's colour. Same box, different file - no
 * URLs change, no layout shifts, and the PDP behind the card agrees because
 * color-pref.js selects the same colour there.
 *
 * The card's HOVER picture gets that colour's own second picture (the map's
 * `hover`, baked from the gallery order and the colour links). It used to get
 * the colour's picture again, so a repainted card hovered onto nothing
 * (Dorian 2026-10-05). No second picture in that colour -> as before.
 *
 * Fail-quiet everywhere: unknown shopper, missing map, product without that
 * colour, or an image that will not load -> the default card image stays.
 * Kill switch: window.AGB_COLOUR_CARDS_OFF. Map seam:
 * window.AGB_COLOUR_MAP_URL (per store).
 */
(function () {
  'use strict';

  if (window.AGB_COLOUR_CARDS_OFF) return;

  var MAP_URL = window.AGB_COLOUR_MAP_URL ||
    'https://storage.googleapis.com/agilite-media-storefront/feeds/agilite-colour-map-row.json';
  // v2: the map gained `hover` - a new key so a 6-hour-old cached map
  // without it is not kept; the old entry is dropped
  var CACHE_KEY = 'SE_colourMap2';
  try { localStorage.removeItem('SE_colourMap'); } catch (e) {}
  var TTL_MS = 6 * 60 * 60 * 1000;
  var map = null;         // { handle: { colourLower: { variant, image, hover? } } }
  var colours = null;     // the shopper's colours, lowercased, in order

  function loadCachedMap() {
    try {
      var c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (!c || !c.fetchedAt || (Date.now() - c.fetchedAt) > TTL_MS) return null;
      return c.products || null;
    } catch (e) { return null; }
  }

  function fetchMap() {
    var cached = loadCachedMap();
    if (cached) { map = cached; sweep(); return; }
    fetch(MAP_URL, { mode: 'cors' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.products) return;
        map = data.products;
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            fetchedAt: Date.now(), products: map
          }));
        } catch (e) {}
        sweep();
      })
      .catch(function () {});
  }

  function handleFrom(href) {
    var m = /\/products\/([a-z0-9-]+)/i.exec(href || '');
    return m ? m[1].toLowerCase() : null;
  }

  function pictureFor(handle) {
    var entry = map && map[handle];
    if (!entry) return null;
    for (var i = 0; i < colours.length; i++) {
      if (entry[colours[i]]) return entry[colours[i]];
    }
    return null;
  }

  // THE BOX MUST NOT MOVE (Dorian 2026-08-24): the replacement is a product
  // shot whose intrinsic size differs from the original - it once BALLOONED a
  // section to the new image's height. Freeze the original frame: same aspect
  // everywhere the layout derives height from the image, and letterbox the
  // newcomer inside it. False when the frame cannot be measured yet.
  function freeze(img) {
    var r = img.getBoundingClientRect();
    if (!(r.width > 0 && r.height > 0)) return false;
    img.style.aspectRatio = (r.width / r.height).toFixed(4);
    img.style.width = '100%';
    img.style.height = 'auto';
    img.style.objectFit = 'contain';
    if (!img.style.backgroundColor) img.style.backgroundColor = '#fff';
    return true;
  }

  function swap(img, url) {
    if (img.dataset.colourCard) return;
    img.dataset.colourCard = '1';
    // keep the original so a load failure falls straight back
    var prevSrc = img.currentSrc || img.src;
    var probe = new Image();
    probe.onload = function () {
      if (freeze(img)) {
        img.removeAttribute('width');
        img.removeAttribute('height');
      }
      img.removeAttribute('srcset');
      img.removeAttribute('data-srcset');
      img.src = url;
    };
    probe.onerror = function () { img.src = prevSrc; };
    probe.src = url;
  }

  // A CARD THAT ARRIVES ONCE THE COLOUR IS KNOWN IS NEVER SEEN IN ANOTHER
  // (Dorian 2026-10-11: "When i'm researching a product, the products are
  // constantly repainting themselves as 'preferred color' its annoying to
  // see"). Measured on live with Black preferred: the search drawer rendered
  // its 8 cards and 0.4 s later every one flipped from Multicam to Black -
  // again for every new query. swap() waits for the new picture while the
  // default one shows, and the observer below waited 400 ms on top. A card
  // added after the shopper's colour and the map are both in hand now gets
  // its picture in the MutationObserver callback, which runs before the
  // browser paints the inserted card: the default picture is never drawn.
  // A picture that fails to load puts the default back. Cards already on the
  // page when the colour arrives (a collection's first load) still swap as
  // before - they were drawn before anyone knew the colour.
  function paintNow(img, url) {
    if (img.dataset.colourCard) return;
    img.dataset.colourCard = '1';
    var prevSrc = img.getAttribute('src');
    var prevSrcset = img.getAttribute('srcset');
    if (freeze(img)) {
      img.removeAttribute('width');
      img.removeAttribute('height');
    }
    img.removeAttribute('srcset');
    img.removeAttribute('data-srcset');
    img.addEventListener('error', function back() {
      img.removeEventListener('error', back);
      if (prevSrcset) img.setAttribute('srcset', prevSrcset);
      if (prevSrc) img.src = prevSrc;
    });
    img.src = url;
  }

  var CARD_LINKS = '.product-card a[href*="/products/"], .horizontal-product-card a[href*="/products/"]';

  // one card link: its picture (and the theme's hover picture) in the colour
  function paintCard(anchor, now) {
    var handle = handleFrom(anchor.getAttribute('href'));
    if (!handle) return;
    var hit = pictureFor(handle);
    if (!hit || !hit.image) return;
    var imgs = anchor.querySelectorAll('img');
    for (var j = 0; j < imgs.length && j < 2; j++) {
      // the theme's hover picture is the card's SECONDARY image
      var isHover = /(^|\s)product-card__image--secondary(\s|$)/.test(imgs[j].className);
      (now ? paintNow : swap)(imgs[j], isHover && hit.hover ? hit.hover : hit.image);
    }
  }

  // the card links an added node brings: inside it, itself, or the card it
  // landed in (a picture inserted into a card already on the page)
  function cardsIn(node) {
    var out = [].slice.call(node.querySelectorAll ? node.querySelectorAll(CARD_LINKS) : []);
    if (node.matches && node.matches(CARD_LINKS)) out.push(node);
    var host = node.closest && node.closest(CARD_LINKS);
    if (host) out.push(host);
    return out;
  }

  function sweep() {
    if (!map || !colours || !colours.length) return;
    // PRODUCT CARDS ONLY. This used to take every link into a product as a
    // card, and the homepage slideshow's slides ARE such links: the K-Zero
    // banner became a plate-carrier cut-out on white, in the shopper's
    // colour, and the Magnetix banner a belt (Dorian 2026-09-09: "we didn't
    // wire products to the top emplacement"). A card is the theme's own
    // <product-card> or its horizontal form (cart cross-sell, complementary
    // products, search results); a slide, a banner, an image-with-text and a
    // cart line item are not, and keep the picture the merchant chose.
    var anchors = document.querySelectorAll(CARD_LINKS);
    for (var i = 0; i < anchors.length; i++) paintCard(anchors[i], false);
  }

  function start(prefColours) {
    colours = prefColours.map(function (c) { return String(c).toLowerCase(); });
    if (map) { sweep(); } else { fetchMap(); }
    // collections render cards as you scroll - keep sweeping, gently
    if (typeof MutationObserver === 'function') {
      var pending = 0;
      new MutationObserver(function (records) {
        // cards that just arrived: painted before they are drawn (see paintNow)
        if (map && colours && colours.length) {
          for (var r = 0; r < records.length; r++) {
            var added = records[r].addedNodes;
            for (var a = 0; a < added.length; a++) {
              if (added[a].nodeType !== 1) continue;
              var cards = cardsIn(added[a]);
              for (var k = 0; k < cards.length; k++) paintCard(cards[k], true);
            }
          }
        }
        // and a gentle sweep for anything that slipped through
        if (pending) return;
        pending = setTimeout(function () { pending = 0; sweep(); }, 400);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  // the map is shopper-independent - fetching it in PARALLEL with the
  // colour lookup halves the first-visit wait (it used to queue behind it)
  fetchMap();

  if (window.SE_colorPref && window.SE_colorPref.colors && window.SE_colorPref.colors.length) {
    start(window.SE_colorPref.colors);
  } else {
    window.addEventListener('SE_colorPrefReady', function (event) {
      var d = (event && event.detail) || {};
      if (d.colors && d.colors.length && !colours) start(d.colors);
    });
  }
})();
